import React from 'react';
import {
  Button, SimpleForm, DateInput, SelectArrayInput, SelectInput, Toolbar,
  TextField, Datagrid, Edit, Create, List, DateField, Filter, TextInput, SaveButton, FormDataConsumer,
  ReferenceArrayInput, AutocompleteArrayInput
} from 'react-admin';
import DeleteWithConfirmation from './components/DeleteWithConfirmation';
import RunReportIcon from '@material-ui/icons/PlayCircleFilledRounded';
import CloudDownload from '@material-ui/icons/CloudDownload';
import DataTable from 'react-data-table-component';
import { fetchUtils } from 'react-admin';

import { ExpandMore } from '@material-ui/icons';
import { Accordion, AccordionSummary, AccordionDetails, Box, Card } from '@material-ui/core';
import FilterCardTitle from './common/filters/FilterCardTitle';
import { NoBidChartComponent } from './components/chart.js';

const utils = require('./components/utils');
const dataSupport = require('./components/data_support.js');
const fetch = fetchUtils.fetchJson;

const BASE_URL = window.nobid_endpoint + '/traffic_reports_report';

const chartMetricsChoices = [
  { id: 'events', name: '#Events' },
];

const chartFormatChoices = [
  { id: 'line', name: 'Line' },
  { id: 'spline', name: 'Smoothed' },
  { id: 'column', name: 'Bars' },
  { id: 'area', name: 'Area' },
];

function downloadCSV (array) {
  array.forEach((row, i) => {
    delete array[i].id;
    delete array[i].code;
  });
  dataSupport.exportData(array);
}

const choicesInterval = [
  { id: 'day', name: 'Daily' },
  { id: 'hour', name: 'Hourly' },
];

const choicesShow = [
  { id: 'date', name: 'Date' },
  { id: 'hour', name: 'Hour' },
  { id: 'type', name: 'Type' },
  { id: 'publisher', name: 'Publisher' },
  { id: 'site', name: 'Site' },
  { id: 'bidder', name: 'Bidder' },
  { id: 'tldomain', name: 'Domain' },
  { id: 'method', name: 'Method' },
];

const dateRangeChoices = [
  { id: '0', name: 'Today' },
  { id: 1, name: 'Yesterday' },
  { id: 2, name: 'Last 2 Days' },
  { id: 3, name: 'Last 3 Days' },
  { id: 5, name: 'Last 5 Days' },
  { id: 7, name: 'Last 7 Days' },
  { id: 10, name: 'Last 10 Days' },
  { id: 15, name: 'Last 15 Days' }
];

const TYPE_CHOICES = [
  { id: 'incoming_request', name: 'Incoming Requests' },
  { id: 'bidrequest', name: 'Bid Requests' },
  { id: 'bidresponse', name: 'Bid Responses' },
];

const TYPE_LABELS = {
  incoming_request: 'Incoming Requests',
  bidrequest: 'Bid Requests',
  bidresponse: 'Bid Responses',
};

const BASE_COLUMNS = [
  { name: 'Date', selector: 'date', sortable: true },
  { name: 'Hour', selector: 'hour', sortable: true },
  { name: 'Type', selector: 'type', sortable: true, cell: row => TYPE_LABELS[row.type] || row.type },
  { name: 'Publisher', selector: 'publisher', sortable: true, cell: row => row.publisher_name ? `${row.publisher_name} (${row.publisher})` : row.publisher },
  { name: 'Site', selector: 'site', sortable: true, cell: row => row.site_name ? `${row.site_name} (${row.site})` : row.site },
  { name: 'Bidder', selector: 'bidder', sortable: true, cell: row => row.bidder_name ? `${row.bidder_name} (${row.bidder})` : row.bidder },
  { name: 'Domain', selector: 'tldomain', sortable: true },
  { name: 'Method', selector: 'method', sortable: true },
  { name: '#Events', selector: 'events', sortable: true, cell: row => <span>{utils.formatLong(row.events)}</span> },
];

let formData = {};
function setFormData (formdata) {
  formData = formdata;
}

// Aggregate rows by date, summing events, so the chart shows a single line
function aggregateByDate (rows) {
  if (!rows || rows.length === 0) return [];
  const map = {};
  rows.forEach(row => {
    const key = row.date || '';
    if (!key) return;
    if (!map[key]) {
      map[key] = { date: key, events: 0 };
    }
    map[key].events += (Number(row.events) || 0);
  });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}

const hasActiveFilters = (record) => {
  const filters = ['type', 'publisher_id', 'site_id', 'bidder_id', 'tldomain'];
  return record && filters.some(f => record[f] && record[f].length > 0);
};

class CustomToolbar extends React.Component {
  state = {
    loading: false,
    baseUrl: BASE_URL,
  };

  constructor (props) {
    super(props);
    this.parent = props.parent;
  }

  updateChart = function (options) {
    if (!this) return;
    // Aggregate data by date only (sum events) so the chart shows a single line
    const chartData = aggregateByDate(this.parent.state.data);
    this.parent.refs.nobidChart.refreshChart(chartData, chartMetricsChoices, chartFormatChoices, options);
  }

  fetchData = () => {
    function updateState (that, headers, json) {
      const rows = (json && json.data) ? json.data : [];
      // If rows have 'hour' but no 'date', copy hour to date for the chart x-axis
      rows.forEach(row => {
        if (!row.date && row.hour) row.date = row.hour;
      });
      const showList = formData.show || [];
      const columnSelectors = showList.concat(['events']);
      that.parent.setState({
        data: rows,
        saved_data: rows,
        columns: BASE_COLUMNS.filter(c => columnSelectors.includes(c.selector)),
        loading: false,
      });
      that.updateChart({ metric: formData.metric || 'events', format: formData.format || 'line' });
    }

    formData.show = formData.show || [];
    // Always include date dimension for the chart x-axis
    const showWithDate = formData.show.includes('date') || formData.show.includes('hour')
      ? formData.show
      : ['date'].concat(formData.show);
    let show = '';
    showWithDate.forEach(item => { show += '&show=' + item; });

    this.parent.setState({ loading: true });
    let url = `${this.state.baseUrl}?${show}` +
      `${utils.formDataFieldsQueryStringFormat(
        formData,
        ['interval', 'date_range', 'start_date', 'end_date', 'type', 'publisher_id', 'site_id', 'bidder_id', 'tldomain']
      )}`;
    const requestOptions = { headers: new Headers() };
    requestOptions.headers.set('accept', 'application/json');
    const token = localStorage.getItem('token');
    requestOptions.headers.set('Authorization', `Bearer ${token}`);
    fetch(url, requestOptions).then(({ headers, json }) => {
      updateState(this, headers, json);
    }).catch(() => {
      this.parent.setState({ data: [], saved_data: [], columns: [], loading: false });
    });
  }

  handleClick = () => {
    this.fetchData();
  }

  render () {
    return (
      <Toolbar>
        <SaveButton style={{ marginRight: '25px' }} variant='contained'
          color='primary' disabled={this.parent.state.loading} {...this.props} />
        <Button label="Run Report" onClick={this.handleClick} props={this.props} variant="contained"
          style={{ marginRight: '25px', padding: '7px', width: '160px' }} color="primary" startIcon={<RunReportIcon />} disabled={this.parent.state.loading} />
      </Toolbar>
    );
  }
}

const TrafficReportTitle = ({ record }) => {
  return <span>Traffic Report {record ? `"${record.name}"` : ''}</span>;
};

const TrafficReportFilters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search_name" alwaysOn resettable />
  </Filter>
);

export const TrafficReportList = ({ permissions, ...props }) => (
  <List {...props} title="Traffic Reports" perPage={25} bulkActionButtons={false} filters={<TrafficReportFilters />}
    exporter={false}>
    <Datagrid rowClick="edit">
      <TextField source="name" label="Name" />
      <DateField source="updated_at" label="Update Date" />
      <DateField source="created_at" label="Creation Date" />
      <DeleteWithConfirmation {...props} />
    </Datagrid>
  </List>
);

class TrafficReportComponent extends React.Component {
  state = {
    data: [],
    loading: false,
    columns: [],
    dateRange: this.props.record.date_range,
  };

  updateChart = function (options) {
    const chartData = aggregateByDate(this.state.data);
    this.refs.nobidChart.refreshChart(chartData, chartMetricsChoices, chartFormatChoices, options);
  }

  handleChartFormatChanged = (value) => {
    this.updateChart({ format: value.target.value });
  }

  render () {
    const { loading, data, dateRange } = this.state;
    const { formType, record } = this.props;

    return (
      <div>
        <Card>
          <SimpleForm toolbar={<CustomToolbar parent={this} />} style={{ width: '100%', backgroundColor: 'inherit', borderRadius: '5px' }} {...this.props}>
            <TextInput source="name" label="Report Name" style={{ width: '400px' }} />
            <div style={{ width: '100%', overflow: 'hidden', display: 'flex', columnGap: '1em' }} id="wrapper2">
              <FormDataConsumer>
                {({ formData }) => setFormData(formData)}
              </FormDataConsumer>
              <SelectInput label="Interval" source="interval" choices={choicesInterval} defaultValue='day'
                disabled={loading} />
              <div>
                <SelectInput label="Date Range" source="date_range"
                  onChange={value => this.setState({ dateRange: value.target.value })}
                  choices={dateRangeChoices}
                  defaultValue="7"
                  disabled={loading} />
                <div style={{ float: 'right', paddingLeft: '5px', display: ('custom' !== dateRange) ? 'none' : 'block' }}>
                  <div style={{ float: 'left', paddingRight: '5px', display: ('custom' !== dateRange) ? 'none' : 'block' }}>
                    <DateInput label="Start Date" source="start_date" allowEmpty disabled={loading}
                      style={{ display: ('custom' !== dateRange) ? 'none' : 'block' }} />
                  </div>
                  <div style={{ float: 'left', display: ('custom' !== dateRange) ? 'none' : 'block' }}>
                    <DateInput label="End Date" source="end_date" allowEmpty disabled={loading}
                      style={{ display: ('custom' !== dateRange) ? 'none' : 'block' }} />
                  </div>
                </div>
              </div>
              <SelectArrayInput label="Show"
                size="small"
                source="show"
                choices={choicesShow}
                style={{ width: '400px', flexGrow: 1 }}
                disabled={loading} />
            </div>

            <Accordion defaultExpanded={formType === 'create'} style={{ marginBottom: '20px' }} fullWidth>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <FilterCardTitle title="Filters" values={hasActiveFilters(record)} />
              </AccordionSummary>
              <AccordionDetails>
                <Box style={{ width: '100%' }}>
                  <div style={{ width: '100%', overflow: 'hidden', display: 'flex', columnGap: '1em' }}>
                    <div style={{ flex: 1 }}>
                      <SelectArrayInput label="Type"
                        source="type"
                        choices={TYPE_CHOICES}
                        disabled={loading}
                        fullWidth />
                    </div>
                    <div style={{ flex: 1 }}>
                      <ReferenceArrayInput
                        source="publisher_id"
                        label="Publisher"
                        reference="publishers"
                        basePath="publishers"
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty
                        disabled={loading}>
                        <AutocompleteArrayInput disabled={loading}
                          label="Publishers"
                          optionText="name"
                          optionValue="code"
                          fullWidth />
                      </ReferenceArrayInput>
                    </div>
                    <div style={{ flex: 1 }}>
                      <ReferenceArrayInput
                        source="site_id"
                        label="Site"
                        reference="sites"
                        basePath="sites"
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty
                        disabled={loading}>
                        <AutocompleteArrayInput disabled={loading}
                          label="Sites"
                          optionText={x => x && x.name ? `${x.name} (${x.code})` : ''}
                          optionValue="code"
                          fullWidth />
                      </ReferenceArrayInput>
                    </div>
                    <div style={{ flex: 1 }}>
                      <ReferenceArrayInput
                        source="bidder_id"
                        label="Bidder"
                        reference="bidders"
                        basePath="bidders"
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty
                        disabled={loading}>
                        <AutocompleteArrayInput disabled={loading}
                          label="Bidders"
                          optionText={record => (record && record.name) ? `${record.name} (${record.code})` : ''}
                          optionValue="code"
                          fullWidth />
                      </ReferenceArrayInput>
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextInput label="Domain" source="tldomain" disabled={loading}
                        helperText="Partial match" fullWidth />
                    </div>
                  </div>
                </Box>
              </AccordionDetails>
            </Accordion>
            <div style={{ width: '100%', overflow: 'hidden' }}>
              <div style={{ float: 'right' }}>
                <SelectInput label="Chart Format"
                  source='format'
                  choices={chartFormatChoices}
                  defaultValue={'line'}
                  onChange={this.handleChartFormatChanged}
                  disabled={loading}
                  style={{ paddingRight: '5px' }}
                />
              </div>
            </div>
          </SimpleForm>
        </Card>
        <div style={{ height: '15px', backgroundColor: 'transparent' }} />
        <Card>
          <NoBidChartComponent ref="nobidChart" data={data} />
        </Card>
        <div style={{ height: '15px', backgroundColor: 'transparent' }} />
        <Card>
          <DataTable
            title="Traffic Report"
            theme={window.nobid_table_theme()}
            columns={this.state.columns}
            data={data}
            progressPending={loading}
            pagination
            fixedHeader
            paginationPerPage={30}
            paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
            dense
            highlightOnHover
            subHeader={!loading}
            subHeaderComponent={
              (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="text" id='search_box' label="Search"
                    variant="outlined"
                    style={{ margin: '5px' }}
                    onChange={(target) => dataSupport.filterData(this)} />
                  <CloudDownload
                    label="Export"
                    style={{ margin: '5px' }}
                    color="action"
                    onClick={() => downloadCSV(this.state.data)}
                  />
                </div>
              )
            }
          />
        </Card>
      </div>
    );
  }
}

export const TrafficReportEdit = (props) => (
  <Edit title={<TrafficReportTitle />} {...props}>
    <TrafficReportComponent formType="edit" permissions={props.permissions} {...props} />
  </Edit>
);

export const TrafficReportCreate = (props) => (
  <Create title="Create Traffic Report" {...props}>
    <TrafficReportComponent formType="create" permissions={props.permissions} {...props} />
  </Create>
);
